/**
 * These are all the applications that live in the alli sidebar, they are in
 * the "common" app because we need to track the current application based on
 * the microfrontend rendered in the the content area.
 *
 * When those render they _may_ call a `setCurrentApplication` from this library
 * with one of the constants below. This declare that the client switcher in
 * the header should send users to this client and declares which sidebar item
 * is active.
 *
 * By default the application tries to make itself active in the sidebar, (eg the
 * `id` attribute is used). However, the `activeInSidebar` prop may be set to
 * keep another item active.
 *
 * These application objects should be treated as opaque. Know that you may get
 * a current application object, but pass it to the functions provided here
 * to make it work.
 */

const alliDomain = process.env.ALLI_DOMAIN; // eslint-disable-line no-undef
const clientPlaceholder = '{{CLIENT}}';

export function clientUrl(...path) {
    let url = `/client/${clientPlaceholder}`;
    if (path.length > 0) {
        url += `/${path.join('/')}`;
    }
    return url.replace(/\/+$/, '');
}

export const HOMECOURT = 'homecourt';
export const homecourt = {
    id: HOMECOURT,
    name: 'Home',
    clientUrl: clientUrl('home'),
    isSpa: true,
    iconPath: (
        <path
            d="M1 10L3 8M3 8L10 1L17 8M3 8V18C3 18.5523 3.44772 19 4 19H7M17 8L19 10M17 8V18C17 18.5523 16.5523 19 16 19H13M7 19C7.55228 19 8 18.5523 8 18V14C8 13.4477 8.44772 13 9 13H11C11.5523 13 12 13.4477 12 14V18C12 18.5523 12.4477 19 13 19M7 19H13"
            stroke="currentColor"
            stroke-width="1.5"
        />
    ),
    subNavItems: {},
    subNav: [],
};

export const DASHBOARDS = 'dashboards';
export const GENERATIVE_DASHBOARDS = 'native-visualizations';
export const generativeDashboards = {
    id: GENERATIVE_DASHBOARDS,
    name: 'Generative',
    clientUrl: clientUrl('native-visualizations'),
    isSpa: true,
};
export const dashboards = {
    id: DASHBOARDS,
    name: 'Dashboards',
    clientUrl: clientUrl('dashboards'),
    isSpa: true,
    iconPath: (
        <path
            d="M21.675 9.0375C21.1479 7.78994 20.3838 6.6565 19.425 5.7C18.4685 4.74119 17.3351 3.97707 16.0875 3.45C14.7914 2.90156 13.418 2.625 12 2.625C10.582 2.625 9.20859 2.90156 7.9125 3.45C6.66494 3.97707 5.5315 4.74119 4.575 5.7C3.61619 6.6565 2.85207 7.78994 2.325 9.0375C1.77656 10.3336 1.5 11.707 1.5 13.125C1.5 16.2352 2.86641 19.1648 5.24766 21.1664L5.2875 21.1992C5.42344 21.3117 5.59453 21.375 5.77031 21.375H18.232C18.4078 21.375 18.5789 21.3117 18.7148 21.1992L18.7547 21.1664C21.1336 19.1648 22.5 16.2352 22.5 13.125C22.5 11.707 22.2211 10.3336 21.675 9.0375ZM17.8453 19.5938H6.15469C5.24905 18.7772 4.52524 17.7793 4.03021 16.6649C3.53518 15.5505 3.28001 14.3444 3.28125 13.125C3.28125 10.7953 4.18828 8.60625 5.83594 6.96094C7.48359 5.31328 9.67266 4.40625 12 4.40625C14.3297 4.40625 16.5188 5.31328 18.1641 6.96094C19.8117 8.60859 20.7188 10.7977 20.7188 13.125C20.7188 15.6 19.6758 17.9414 17.8453 19.5938ZM14.6133 9.87891C14.578 9.84401 14.5305 9.82444 14.4809 9.82444C14.4313 9.82444 14.3837 9.84401 14.3484 9.87891L12.368 11.8594C11.9297 11.7422 11.4445 11.8547 11.1 12.1992C10.978 12.321 10.8812 12.4657 10.8151 12.6249C10.749 12.7842 10.715 12.9549 10.715 13.1273C10.715 13.2998 10.749 13.4705 10.8151 13.6297C10.8812 13.789 10.978 13.9337 11.1 14.0555C11.2218 14.1775 11.3665 14.2743 11.5257 14.3404C11.685 14.4064 11.8557 14.4404 12.0281 14.4404C12.2005 14.4404 12.3713 14.4064 12.5305 14.3404C12.6898 14.2743 12.8344 14.1775 12.9562 14.0555C13.1195 13.8927 13.2369 13.6898 13.2965 13.4671C13.3562 13.2445 13.3561 13.0101 13.2961 12.7875L15.2766 10.807C15.3492 10.7344 15.3492 10.6148 15.2766 10.5422L14.6133 9.87891ZM11.4844 7.5H12.5156C12.6187 7.5 12.7031 7.41563 12.7031 7.3125V5.4375C12.7031 5.33437 12.6187 5.25 12.5156 5.25H11.4844C11.3813 5.25 11.2969 5.33437 11.2969 5.4375V7.3125C11.2969 7.41563 11.3813 7.5 11.4844 7.5ZM17.5781 12.6094V13.6406C17.5781 13.7437 17.6625 13.8281 17.7656 13.8281H19.6406C19.7437 13.8281 19.8281 13.7437 19.8281 13.6406V12.6094C19.8281 12.5063 19.7437 12.4219 19.6406 12.4219H17.7656C17.6625 12.4219 17.5781 12.5063 17.5781 12.6094ZM17.8758 7.9875L17.1469 7.25859C17.1116 7.2237 17.064 7.20413 17.0145 7.20413C16.9649 7.20413 16.9173 7.2237 16.882 7.25859L15.5555 8.58516C15.5206 8.6204 15.501 8.66799 15.501 8.71758C15.501 8.76717 15.5206 8.81476 15.5555 8.85L16.2844 9.57891C16.357 9.65156 16.4766 9.65156 16.5492 9.57891L17.8758 8.25234C17.9484 8.17969 17.9484 8.06016 17.8758 7.9875ZM7.12734 7.25859C7.0921 7.2237 7.04452 7.20412 6.99492 7.20413C6.94533 7.20412 6.89774 7.2237 6.8625 7.25859L6.13359 7.9875C6.0987 8.02274 6.07912 8.07033 6.07913 8.11992C6.07912 8.16952 6.0987 8.2171 6.13359 8.25234L7.46016 9.57891C7.53281 9.65156 7.65234 9.65156 7.725 9.57891L8.45391 8.85C8.52656 8.77734 8.52656 8.65781 8.45391 8.58516L7.12734 7.25859ZM6.14062 12.4219H4.26562C4.1625 12.4219 4.07812 12.5063 4.07812 12.6094V13.6406C4.07812 13.7437 4.1625 13.8281 4.26562 13.8281H6.14062C6.24375 13.8281 6.32812 13.7437 6.32812 13.6406V12.6094C6.32812 12.5063 6.24375 12.4219 6.14062 12.4219Z"
            className="fill-current"
        />
    ),
    subNavItems: {
        [DASHBOARDS]: {
            id: DASHBOARDS,
            name: 'Pinned',
            clientUrl: clientUrl('dashboards'),
            isSpa: true,
        },
        [GENERATIVE_DASHBOARDS]: generativeDashboards,
    },
    subNav: [DASHBOARDS, GENERATIVE_DASHBOARDS],
};

export const DATA_EXPLORER = 'dataExplorer';
export const DATA_SOURCES = 'data.sources';
export const DATA_CATEGORIZATIONS = 'data.categorizations';
export const DATA_REPORTS = 'data.reports';
export const DATA_ALERTS = 'data.alerts';
export const DATA = 'data';
export const data = {
    id: DATA,
    name: 'Reporting',
    alli360Name: 'Data',
    clientUrl: `https://data.${alliDomain}/project/${clientPlaceholder}/datasources`,
    isSpa: false,
    subNavItems: {
        [DATA_SOURCES]: {
            id: DATA_SOURCES,
            name: 'Sources',
            clientUrl: `https://data.${alliDomain}/project/${clientPlaceholder}/datasources`,
            isSpa: false,
        },
        [DATA_CATEGORIZATIONS]: {
            id: DATA_CATEGORIZATIONS,
            name: 'Categorizations',
            clientUrl: `https://data.${alliDomain}/project/${clientPlaceholder}/categorization`,
            isSpa: false,
        },
        [DATA_EXPLORER]: {
            id: DATA_EXPLORER,
            name: 'Explorer',
            clientUrl: clientUrl('data-explorer'),
            isSpa: true,
        },
        [DATA_REPORTS]: {
            id: DATA_REPORTS,
            name: 'Reports',
            clientUrl: clientUrl('data-explorer', 'reports'),
            isSpa: true,
        },
        [DATA_ALERTS]: {
            id: DATA_ALERTS,
            name: 'Alerts',
            clientUrl: clientUrl('data-explorer', 'alerts'),
            isSpa: true,
        },
    },
    subNav: [
        DATA_SOURCES,
        DATA_CATEGORIZATIONS,
        DATA_EXPLORER,
        DATA_REPORTS,
        DATA_ALERTS,
    ],
    iconPath: (
        <path
            d="M20.8125 18.5625H4.6875V3.9375C4.6875 3.83437 4.60312 3.75 4.5 3.75H3.1875C3.08437 3.75 3 3.83437 3 3.9375V20.0625C3 20.1656 3.08437 20.25 3.1875 20.25H20.8125C20.9156 20.25 21 20.1656 21 20.0625V18.75C21 18.6469 20.9156 18.5625 20.8125 18.5625ZM6.75 16.6875H8.0625C8.16563 16.6875 8.25 16.6031 8.25 16.5V13.125C8.25 13.0219 8.16563 12.9375 8.0625 12.9375H6.75C6.64687 12.9375 6.5625 13.0219 6.5625 13.125V16.5C6.5625 16.6031 6.64687 16.6875 6.75 16.6875ZM10.3125 16.6875H11.625C11.7281 16.6875 11.8125 16.6031 11.8125 16.5V9C11.8125 8.89687 11.7281 8.8125 11.625 8.8125H10.3125C10.2094 8.8125 10.125 8.89687 10.125 9V16.5C10.125 16.6031 10.2094 16.6875 10.3125 16.6875ZM13.875 16.6875H15.1875C15.2906 16.6875 15.375 16.6031 15.375 16.5V10.8281C15.375 10.725 15.2906 10.6406 15.1875 10.6406H13.875C13.7719 10.6406 13.6875 10.725 13.6875 10.8281V16.5C13.6875 16.6031 13.7719 16.6875 13.875 16.6875ZM17.4375 16.6875H18.75C18.8531 16.6875 18.9375 16.6031 18.9375 16.5V7.125C18.9375 7.02187 18.8531 6.9375 18.75 6.9375H17.4375C17.3344 6.9375 17.25 7.02187 17.25 7.125V16.5C17.25 16.6031 17.3344 16.6875 17.4375 16.6875Z"
            className="fill-current"
        />
    ),
};

export const dataExplorer = {
    id: DATA_EXPLORER,
    name: 'Data Explorer',
    clientUrl: clientUrl('data-explorer'),
    activeInSidebar: DATA,
    activeSubnavItem: DATA_EXPLORER,
    isSpa: true,
    iconPath: (
        <path
            d="M20.8125 18.5625H4.6875V3.9375C4.6875 3.83437 4.60312 3.75 4.5 3.75H3.1875C3.08437 3.75 3 3.83437 3 3.9375V20.0625C3 20.1656 3.08437 20.25 3.1875 20.25H20.8125C20.9156 20.25 21 20.1656 21 20.0625V18.75C21 18.6469 20.9156 18.5625 20.8125 18.5625ZM6.75 16.6875H8.0625C8.16563 16.6875 8.25 16.6031 8.25 16.5V13.125C8.25 13.0219 8.16563 12.9375 8.0625 12.9375H6.75C6.64687 12.9375 6.5625 13.0219 6.5625 13.125V16.5C6.5625 16.6031 6.64687 16.6875 6.75 16.6875ZM10.3125 16.6875H11.625C11.7281 16.6875 11.8125 16.6031 11.8125 16.5V9C11.8125 8.89687 11.7281 8.8125 11.625 8.8125H10.3125C10.2094 8.8125 10.125 8.89687 10.125 9V16.5C10.125 16.6031 10.2094 16.6875 10.3125 16.6875ZM13.875 16.6875H15.1875C15.2906 16.6875 15.375 16.6031 15.375 16.5V10.8281C15.375 10.725 15.2906 10.6406 15.1875 10.6406H13.875C13.7719 10.6406 13.6875 10.725 13.6875 10.8281V16.5C13.6875 16.6031 13.7719 16.6875 13.875 16.6875ZM17.4375 16.6875H18.75C18.8531 16.6875 18.9375 16.6031 18.9375 16.5V7.125C18.9375 7.02187 18.8531 6.9375 18.75 6.9375H17.4375C17.3344 6.9375 17.25 7.02187 17.25 7.125V16.5C17.25 16.6031 17.3344 16.6875 17.4375 16.6875Z"
            className="fill-current"
        />
    ),
};

export const CLIENT_INTAKE_BRIEF = 'strategyAndPlanning.clientIntakeBrief';
export const AUDIENCE_PLANNER = 'strategyAndPlanning.audiencePlanning';
export const COMMS_PLANNER = 'strategyAndPlanning.commsPlanning';
export const PLANNING_OVERVIEW = 'strategyAndPlanning.planningOverview';
export const MEDIA_PLANNER = 'strategyAndPlanning.mediaPlanning';
export const STRATEGY_AND_PLANNING = 'strategyAndPlanning';
export const strategyAndPlanning = {
    id: STRATEGY_AND_PLANNING,
    name: 'Strategy & Planning',
    clientUrl: clientUrl('planning', 'brief-intake'),
    activeSubnavItem: false,
    isSpa: true,
    iconPath: (
        <path
            stroke-width="1.5"
            stroke="currentColor"
            d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75"
        />
    ),
    subNavItems: {
        [CLIENT_INTAKE_BRIEF]: {
            id: CLIENT_INTAKE_BRIEF,
            name: 'Client Intake Brief',
            clientUrl: clientUrl('planning', 'brief-intake'),
            isSpa: true,
        },
        [AUDIENCE_PLANNER]: {
            id: AUDIENCE_PLANNER,
            name: 'Audience Planner',
            clientUrl: clientUrl('planning', 'audience-planning'),
            isSpa: true,
        },
        [COMMS_PLANNER]: {
            id: COMMS_PLANNER,
            name: 'Comms Planner',
            clientUrl: clientUrl('planning', 'comms-planner'),
            isSpa: true,
        },
        [MEDIA_PLANNER]: {
            id: MEDIA_PLANNER,
            name: 'Media Planner',
            clientUrl: clientUrl('planning', 'overview'),
            isSpa: true,
        },
    },
    subNav: [
        CLIENT_INTAKE_BRIEF,
        AUDIENCE_PLANNER,
        COMMS_PLANNER,
        MEDIA_PLANNER,
    ],
};

export const PLANNING = 'planning';
export const planning = {
    id: PLANNING,
    name: 'Media Plan',
    clientUrl: clientUrl('media-planning'),
    isSpa: true,
    //icon will be updated
    iconPath: (
        <path
            d="M20.8125 18.5625H4.6875V3.9375C4.6875 3.83437 4.60312 3.75 4.5 3.75H3.1875C3.08437 3.75 3 3.83437 3 3.9375V20.0625C3 20.1656 3.08437 20.25 3.1875 20.25H20.8125C20.9156 20.25 21 20.1656 21 20.0625V18.75C21 18.6469 20.9156 18.5625 20.8125 18.5625ZM6.75 16.6875H8.0625C8.16563 16.6875 8.25 16.6031 8.25 16.5V13.125C8.25 13.0219 8.16563 12.9375 8.0625 12.9375H6.75C6.64687 12.9375 6.5625 13.0219 6.5625 13.125V16.5C6.5625 16.6031 6.64687 16.6875 6.75 16.6875ZM10.3125 16.6875H11.625C11.7281 16.6875 11.8125 16.6031 11.8125 16.5V9C11.8125 8.89687 11.7281 8.8125 11.625 8.8125H10.3125C10.2094 8.8125 10.125 8.89687 10.125 9V16.5C10.125 16.6031 10.2094 16.6875 10.3125 16.6875ZM13.875 16.6875H15.1875C15.2906 16.6875 15.375 16.6031 15.375 16.5V10.8281C15.375 10.725 15.2906 10.6406 15.1875 10.6406H13.875C13.7719 10.6406 13.6875 10.725 13.6875 10.8281V16.5C13.6875 16.6031 13.7719 16.6875 13.875 16.6875ZM17.4375 16.6875H18.75C18.8531 16.6875 18.9375 16.6031 18.9375 16.5V7.125C18.9375 7.02187 18.8531 6.9375 18.75 6.9375H17.4375C17.3344 6.9375 17.25 7.02187 17.25 7.125V16.5C17.25 16.6031 17.3344 16.6875 17.4375 16.6875Z"
            className="fill-current"
        />
    ),
};

export const AUTOMATIONS = 'automations';
export const AUTOMATIONS_WORKFLOWS = `${AUTOMATIONS}.workflows`;
export const AUTOMATIONS_TEMPLATES = `${AUTOMATIONS}.templates`;
export const AUTOMATIONS_CREDENTIALS = `${AUTOMATIONS}.credentials`;
export const PERFORMANCE_MAX = 'performanceMax';
export const ACTIONS = 'actions';
export const actions = {
    id: ACTIONS,
    name: 'Actions',
    clientUrl: clientUrl('actions'),
    activeSubnavItem: ACTIONS,
    isSpa: true,
    subNavItems: {
        [AUTOMATIONS_WORKFLOWS]: {
            id: AUTOMATIONS_WORKFLOWS,
            name: 'Workflows',
            clientUrl: clientUrl(AUTOMATIONS, 'workflows'),
            isSpa: true,
        },
        [AUTOMATIONS_TEMPLATES]: {
            id: AUTOMATIONS_TEMPLATES,
            name: 'Templates',
            clientUrl: clientUrl(AUTOMATIONS, 'templates'),
            isSpa: true,
        },
        [AUTOMATIONS_CREDENTIALS]: {
            id: AUTOMATIONS_CREDENTIALS,
            name: 'Credentials',
            clientUrl: clientUrl(AUTOMATIONS, 'credentials'),
            isSpa: true,
        },
        [ACTIONS]: {
            id: ACTIONS,
            name: 'Actions',
            clientUrl: clientUrl('actions'),
            isSpa: true,
        },
        [PERFORMANCE_MAX]: {
            id: PERFORMANCE_MAX,
            name: 'Performance Max',
            clientUrl: clientUrl('performance-max'),
            isSpa: true,
        },
    },
    subNav: [
        AUTOMATIONS_WORKFLOWS,
        AUTOMATIONS_TEMPLATES,
        AUTOMATIONS_CREDENTIALS,
        ACTIONS,
        PERFORMANCE_MAX,
    ],
    iconPath: (
        <path
            d="M19.8749 8.42109H14.7116L19.3546 2.55469C19.4507 2.43047 19.3639 2.25 19.2069 2.25H10.2186C10.153 2.25 10.0897 2.28516 10.0569 2.34375L3.98425 12.832C3.9116 12.9563 4.00066 13.1133 4.14597 13.1133H8.23347L6.13816 21.4945C6.09363 21.6773 6.31394 21.8062 6.44988 21.675L20.0038 8.74219C20.1257 8.62734 20.0436 8.42109 19.8749 8.42109ZM8.86394 17.168L10.2772 11.5195H6.58816L11.0319 3.84609H16.296L11.4139 10.0172H16.3593L8.86394 17.168Z"
            className="fill-current"
        />
    ),
};

// PMax constant is above since the actions menu item needs it.
export const performanceMax = {
    id: PERFORMANCE_MAX,
    name: 'Performance Max',
    clientUrl: clientUrl('performance-max'),
    activeInSidebar: ACTIONS,
    activeSubnavItem: PERFORMANCE_MAX,
    isSpa: true,
    iconPath: (
        <path
            d="M19.8749 8.42109H14.7116L19.3546 2.55469C19.4507 2.43047 19.3639 2.25 19.2069 2.25H10.2186C10.153 2.25 10.0897 2.28516 10.0569 2.34375L3.98425 12.832C3.9116 12.9563 4.00066 13.1133 4.14597 13.1133H8.23347L6.13816 21.4945C6.09363 21.6773 6.31394 21.8062 6.44988 21.675L20.0038 8.74219C20.1257 8.62734 20.0436 8.42109 19.8749 8.42109ZM8.86394 17.168L10.2772 11.5195H6.58816L11.0319 3.84609H16.296L11.4139 10.0172H16.3593L8.86394 17.168Z"
            className="fill-current"
        />
    ),
};

export const AUDIENCES_LISTS = 'audiences.lists';
export const AUDIENCES_SEGMENTS = 'audiences.segments';
export const AUDIENCES_POWER = 'audiences.power_audiences';
export const AUDIENCES_DEPLOYMENTS = 'audiences.deployments';
export const AUDIENCE_GATEWAY = 'pixel';
export const AUDIENCES = 'audiences';
export const audiences = {
    id: AUDIENCES,
    name: 'Audiences',
    clientUrl: clientUrl('audiences'),
    activeSubnavItem: AUDIENCES,
    isSpa: true,
    iconPath: (
        <path
            d="M19.3176 16.4039C18.731 15.8168 18.0455 15.3376 17.2926 14.9883C18.3543 14.1281 19.0317 12.8156 19.0317 11.3437C19.0317 8.74685 16.8661 6.61638 14.2692 6.65622C11.7121 6.69606 9.65199 8.77966 9.65199 11.3437C9.65199 12.8156 10.3317 14.1281 11.3911 14.9883C10.638 15.3373 9.9525 15.8165 9.36605 16.4039C8.08636 17.6859 7.3598 19.3781 7.31293 21.1828C7.3123 21.2078 7.31669 21.2327 7.32583 21.256C7.33497 21.2793 7.34868 21.3005 7.36616 21.3184C7.38363 21.3363 7.40451 21.3505 7.42757 21.3603C7.45063 21.37 7.4754 21.375 7.50043 21.375H8.81293C8.91371 21.375 8.99808 21.2953 9.00043 21.1945C9.04496 19.8351 9.59574 18.5625 10.5637 17.5968C11.059 17.0989 11.6482 16.7041 12.2971 16.4354C12.946 16.1666 13.6418 16.0293 14.3442 16.0312C15.7715 16.0312 17.1145 16.5867 18.1246 17.5968C19.0903 18.5625 19.6411 19.8351 19.6879 21.1945C19.6903 21.2953 19.7746 21.375 19.8754 21.375H21.1879C21.2129 21.375 21.2377 21.37 21.2608 21.3603C21.2838 21.3505 21.3047 21.3363 21.3222 21.3184C21.3397 21.3005 21.3534 21.2793 21.3625 21.256C21.3717 21.2327 21.3761 21.2078 21.3754 21.1828C21.3286 19.3781 20.602 17.6859 19.3176 16.4039ZM14.3442 14.3437C13.5426 14.3437 12.7879 14.032 12.2231 13.4648C11.9396 13.1836 11.7157 12.8481 11.5647 12.4785C11.4137 12.1088 11.3387 11.7125 11.3442 11.3133C11.3512 10.5445 11.6582 9.80153 12.195 9.25075C12.7575 8.67419 13.5098 8.3531 14.3137 8.34372C15.1082 8.33669 15.8793 8.64606 16.4465 9.20153C17.0278 9.77106 17.3465 10.5328 17.3465 11.3437C17.3465 12.1453 17.0348 12.8976 16.4676 13.4648C16.1893 13.7444 15.8584 13.9661 15.4939 14.1169C15.1294 14.2678 14.7386 14.3449 14.3442 14.3437ZM8.47308 11.9625C8.45199 11.7586 8.44027 11.5523 8.44027 11.3437C8.44027 10.9711 8.47543 10.6078 8.54105 10.2539C8.55746 10.1695 8.51293 10.0828 8.43558 10.0476C8.11683 9.90466 7.82386 9.70778 7.57074 9.45935C7.27247 9.17014 7.03777 8.82194 6.88159 8.43696C6.72542 8.05198 6.65121 7.63867 6.66371 7.22341C6.6848 6.47106 6.98714 5.75622 7.51449 5.21716C8.09339 4.62419 8.87152 4.30075 9.69886 4.31013C10.4465 4.31716 11.1684 4.60544 11.7145 5.11638C11.8996 5.28981 12.059 5.482 12.1926 5.68825C12.2395 5.76091 12.3309 5.79138 12.4106 5.76325C12.8231 5.62028 13.259 5.5195 13.7067 5.47263C13.8379 5.45856 13.9129 5.31794 13.8543 5.20075C13.0926 3.69372 11.5364 2.6531 9.73636 2.62497C7.13714 2.58513 4.97152 4.7156 4.97152 7.31013C4.97152 8.782 5.64886 10.0945 6.71058 10.9547C5.96527 11.2992 5.27855 11.775 4.68324 12.3703C3.39886 13.6523 2.6723 15.3445 2.62542 17.1515C2.6248 17.1765 2.62919 17.2014 2.63833 17.2247C2.64747 17.248 2.66118 17.2692 2.67866 17.2872C2.69613 17.3051 2.71701 17.3193 2.74007 17.329C2.76313 17.3387 2.7879 17.3437 2.81292 17.3437H4.12777C4.22855 17.3437 4.31293 17.264 4.31527 17.1632C4.3598 15.8039 4.91058 14.5312 5.87855 13.5656C6.56761 12.8765 7.41136 12.3984 8.33246 12.1664C8.42386 12.1429 8.4848 12.0562 8.47308 11.9625Z"
            className="fill-current"
        />
    ),
    subNavItems: {
        [AUDIENCES_LISTS]: {
            id: AUDIENCES_LISTS,
            name: 'Lists',
            clientUrl: clientUrl('audiences'),
            isSpa: true,
        },
        [AUDIENCES_SEGMENTS]: {
            id: AUDIENCES_SEGMENTS,
            name: 'Segments',
            clientUrl: clientUrl('audiences', 'segments'),
            isSpa: true,
        },
        [AUDIENCES_POWER]: {
            id: AUDIENCES_POWER,
            name: 'Power Audiences',
            clientUrl: clientUrl('audiences', 'poweraudiences'),
            isSpa: true,
        },
        [AUDIENCES_DEPLOYMENTS]: {
            id: AUDIENCES_DEPLOYMENTS,
            name: 'Deployments',
            clientUrl: clientUrl('audiences', 'deployments'),
            isSpa: true,
        },
        [AUDIENCE_GATEWAY]: {
            id: AUDIENCE_GATEWAY,
            name: 'Audience Gateway',
            clientUrl: clientUrl('pixel'),
            isSpa: true,
            activeInSidebar: AUDIENCES,
            activeSubnavItem: AUDIENCE_GATEWAY,
        },
    },
    subNav: [
        AUDIENCES_LISTS,
        AUDIENCES_SEGMENTS,
        AUDIENCES_POWER,
        AUDIENCES_DEPLOYMENTS,
        AUDIENCE_GATEWAY,
    ],
};
export const pixel = {
    id: AUDIENCE_GATEWAY,
    name: 'Audience Gateway',
    clientUrl: clientUrl('pixel'),
    activeInSidebar: AUDIENCES,
    activeSubnavItem: AUDIENCE_GATEWAY,
    isSpa: true,
};

export const CREATIVE_INSIGHTS = 'creative.creative-insights';
export const CREATIVE_LABS = 'creative.creative-labs';
export const CREATIVE = 'creative';

export const creative = {
    id: CREATIVE,
    name: 'Creative',
    clientUrl: clientUrl('creative'),
    isSpa: true,
    iconPath: (
        <path
            d="M21.75 3.75H2.25C1.83516 3.75 1.5 4.08516 1.5 4.5V19.5C1.5 19.9148 1.83516 20.25 2.25 20.25H21.75C22.1648 20.25 22.5 19.9148 22.5 19.5V4.5C22.5 4.08516 22.1648 3.75 21.75 3.75ZM20.8125 18.5625H3.1875V17.6273L6.43359 13.7766L9.95156 17.9484L15.4242 11.4609L20.8125 17.85V18.5625ZM20.8125 15.5203L15.5672 9.3C15.4922 9.21094 15.3562 9.21094 15.2812 9.3L9.95156 15.6188L6.57656 11.618C6.50156 11.5289 6.36562 11.5289 6.29062 11.618L3.1875 15.2977V5.4375H20.8125V15.5203ZM7.125 10.6875C7.39585 10.6875 7.66405 10.6342 7.91428 10.5305C8.16452 10.4269 8.39189 10.2749 8.58341 10.0834C8.77493 9.89189 8.92685 9.66452 9.0305 9.41428C9.13415 9.16405 9.1875 8.89585 9.1875 8.625C9.1875 8.35415 9.13415 8.08595 9.0305 7.83572C8.92685 7.58548 8.77493 7.35811 8.58341 7.16659C8.39189 6.97507 8.16452 6.82315 7.91428 6.7195C7.66405 6.61585 7.39585 6.5625 7.125 6.5625C6.57799 6.5625 6.05339 6.7798 5.66659 7.16659C5.2798 7.55339 5.0625 8.07799 5.0625 8.625C5.0625 9.17201 5.2798 9.69661 5.66659 10.0834C6.05339 10.4702 6.57799 10.6875 7.125 10.6875ZM7.125 7.96875C7.48828 7.96875 7.78125 8.26172 7.78125 8.625C7.78125 8.98828 7.48828 9.28125 7.125 9.28125C6.76172 9.28125 6.46875 8.98828 6.46875 8.625C6.46875 8.26172 6.76172 7.96875 7.125 7.96875Z"
            className="fill-current"
        />
    ),
    subNavItems: {
        [CREATIVE_INSIGHTS]: {
            id: CREATIVE_INSIGHTS,
            name: 'Creative Insights',
            clientUrl: clientUrl('creative', 'grid'),
            isSpa: true,
        },
        [CREATIVE_LABS]: {
            id: CREATIVE_LABS,
            name: 'Creative Labs',
            clientUrl: clientUrl('creative', 'creative-labs'),
            isSpa: true,
        },
    },
    subNav: [CREATIVE_INSIGHTS, CREATIVE_LABS],
};

export const PRODUCTS_FEEDS = 'products.feeds';
export const PRODUCTS_FILTERS = 'products.filters';
export const PRODUCTS_CONNECTIONS = 'products.connections';
export const PRODUCTS_EXPORTS = 'products.exports';
export const PRODUCTS_MAPPINGS = 'products.output_mappings';
export const PRODUCTS = 'products';
export const products = {
    id: PRODUCTS,
    name: 'Products',
    clientUrl: clientUrl('products'),
    activeSubnavItem: false,
    isSpa: true,
    iconPath: (
        <path
            d="M19.5 7.3125H16.3125V6.9375C16.3125 4.55625 14.3813 2.625 12 2.625C9.61875 2.625 7.6875 4.55625 7.6875 6.9375V7.3125H4.5C4.08516 7.3125 3.75 7.64766 3.75 8.0625V20.625C3.75 21.0398 4.08516 21.375 4.5 21.375H19.5C19.9148 21.375 20.25 21.0398 20.25 20.625V8.0625C20.25 7.64766 19.9148 7.3125 19.5 7.3125ZM9.375 6.9375C9.375 5.48672 10.5492 4.3125 12 4.3125C13.4508 4.3125 14.625 5.48672 14.625 6.9375V7.3125H9.375V6.9375ZM18.5625 19.6875H5.4375V9H7.6875V11.0625C7.6875 11.1656 7.77187 11.25 7.875 11.25H9.1875C9.29063 11.25 9.375 11.1656 9.375 11.0625V9H14.625V11.0625C14.625 11.1656 14.7094 11.25 14.8125 11.25H16.125C16.2281 11.25 16.3125 11.1656 16.3125 11.0625V9H18.5625V19.6875Z"
            className="fill-current"
        />
    ),
    subNavItems: {
        [PRODUCTS_FEEDS]: {
            id: PRODUCTS_FEEDS,
            name: 'Feeds',
            clientUrl: clientUrl('products', 'feeds'),
            isSpa: true,
        },
        [PRODUCTS_FILTERS]: {
            id: PRODUCTS_FILTERS,
            name: 'Filters',
            clientUrl: clientUrl('products', 'filters'),
            isSpa: true,
        },
        [PRODUCTS_CONNECTIONS]: {
            id: PRODUCTS_CONNECTIONS,
            name: 'Connections',
            clientUrl: clientUrl('products', 'connections'),
            isSpa: true,
        },
        [PRODUCTS_EXPORTS]: {
            id: PRODUCTS_EXPORTS,
            name: 'Exports',
            clientUrl: clientUrl('products', 'exports'),
            isSpa: true,
        },
        [PRODUCTS_MAPPINGS]: {
            id: PRODUCTS_MAPPINGS,
            name: 'Output Mappings',
            clientUrl: clientUrl('products', 'output-mappings'),
            isSpa: true,
        },
    },
    subNav: [
        PRODUCTS_FEEDS,
        PRODUCTS_FILTERS,
        PRODUCTS_CONNECTIONS,
        PRODUCTS_EXPORTS,
        PRODUCTS_MAPPINGS,
    ],
};

export const MARKETPLACE = 'marketplace';
export const MARKETPLACE_APPS = `${MARKETPLACE}.apps`;
export const MARKETPLACE_BROWSE = `${MARKETPLACE}.browse`;
export const MARKETPLACE_WORKFLOWS = `${MARKETPLACE}.workflows`;

export const marketplace = {
    id: MARKETPLACE,
    name: 'Marketplace',
    clientUrl: clientUrl(MARKETPLACE),
    activeSubnavItem: false,
    isSpa: true,
    iconPath: (
        <path
            d="M12 1.5C6.20156 1.5 1.5 6.20156 1.5 12C1.5 17.7984 6.20156 22.5 12 22.5C17.7984 22.5 22.5 17.7984 22.5 12C22.5 6.20156 17.7984 1.5 12 1.5ZM12 20.7188C7.18594 20.7188 3.28125 16.8141 3.28125 12C3.28125 7.18594 7.18594 3.28125 12 3.28125C16.8141 3.28125 20.7188 7.18594 20.7188 12C20.7188 16.8141 16.8141 20.7188 12 20.7188ZM16.65 6.93516C16.5582 6.89929 16.4603 6.88186 16.3617 6.88387C16.2632 6.88588 16.166 6.90729 16.0758 6.94687L9.72422 9.72656L6.94687 16.0781C6.8625 16.2703 6.8625 16.4883 6.94687 16.6805C7.02656 16.8633 7.17422 17.0063 7.36172 17.0789C7.45078 17.1141 7.54219 17.1305 7.63594 17.1305C7.73906 17.1305 7.83984 17.1094 7.93594 17.0672L14.2875 14.2875L17.0648 7.93594C17.1054 7.84324 17.1268 7.74332 17.1277 7.64216C17.1287 7.541 17.1092 7.44068 17.0705 7.34723C17.0317 7.25377 16.9745 7.16911 16.9023 7.0983C16.83 7.02749 16.7442 6.972 16.65 6.93516ZM13.5187 12.525L14.1328 13.1391L13.1391 14.1328L12.525 13.5187L8.90625 15.1031L10.4883 11.4844L9.89062 10.8844L10.8844 9.89062L11.482 10.4883L15.1031 8.90625L13.5187 12.525ZM10.8844 9.89062L9.89062 10.8844L10.4883 11.4844L12.525 13.5187L13.1391 14.1328L14.1328 13.1391L13.5187 12.525L11.4844 10.4883L10.8844 9.89062Z"
            className="fill-current"
        />
    ),
    subNavItems: {
        [MARKETPLACE_APPS]: {
            id: MARKETPLACE_APPS,
            name: 'Apps',
            clientUrl: clientUrl(MARKETPLACE, 'pallets'),
            isSpa: true,
            version: 1,
        },
        [MARKETPLACE_BROWSE]: {
            id: MARKETPLACE_BROWSE,
            name: 'Browse',
            clientUrl: clientUrl(MARKETPLACE),
            isSpa: true,
            version: 1,
        },
        [MARKETPLACE_WORKFLOWS]: {
            id: MARKETPLACE_WORKFLOWS,
            name: 'Workflows',
            clientUrl: clientUrl(MARKETPLACE, 'workflows'),
            isSpa: true,
            version: 1,
        },
    },
    subNav: [MARKETPLACE_APPS, MARKETPLACE_BROWSE, MARKETPLACE_WORKFLOWS],
};

export const MYAPPS = 'myapps';
export const myapps = {
    id: MYAPPS,
    name: 'My Apps',
    clientUrl: clientUrl('marketplace', 'pallets'),
    isSpa: true,
    hideInAlli360: true,
    iconPath: (
        <path
            d="M21.4688 4.92188H8.8125C8.39766 4.92188 8.0625 5.25703 8.0625 5.67188V11.2031H2.53125C2.11641 11.2031 1.78125 11.5383 1.78125 11.9531V18.3281C1.78125 18.743 2.11641 19.0781 2.53125 19.0781H15.1875C15.6023 19.0781 15.9375 18.743 15.9375 18.3281V12.7969H21.4688C21.8836 12.7969 22.2188 12.4617 22.2188 12.0469V5.67188C22.2188 5.25703 21.8836 4.92188 21.4688 4.92188ZM9.65625 6.51562H14.3438V11.2031H9.65625V6.51562ZM8.0625 17.4844H3.375V12.7969H8.0625V17.4844ZM14.3438 17.4844H9.65625V12.7969H14.3438V17.4844ZM20.625 11.2031H15.9375V6.51562H20.625V11.2031Z"
            className="fill-current"
        />
    ),
};

export const CLOUD_STORAGE = 'cloudStorage';
export const MANAGE_CLIENT = 'manageClient';
export const manageClient = {
    id: MANAGE_CLIENT,
    name: 'Settings',
    clientUrl: clientUrl('manage'),
    isSpa: true,
    subNavItems: {
        [MANAGE_CLIENT]: {
            id: MANAGE_CLIENT,
            name: 'Manage Client',
            clientUrl: clientUrl('manage'),
            isSpa: true,
        },
        [CLOUD_STORAGE]: {
            id: CLOUD_STORAGE,
            name: 'Cloud Storage',
            clientUrl: clientUrl('cloud-storage'),
            isSpa: true,
        },
    },
    subNav: [MANAGE_CLIENT, CLOUD_STORAGE],
    iconPath: (
        <path
            d="M21.675 14.6652L20.1398 13.3527C20.2125 12.9074 20.25 12.4527 20.25 11.9981C20.25 11.5434 20.2125 11.0887 20.1398 10.6434L21.675 9.33087C21.7908 9.23175 21.8737 9.09972 21.9126 8.95235C21.9515 8.80498 21.9447 8.64925 21.893 8.50587L21.8719 8.44493C21.4492 7.26375 20.8164 6.16877 20.0039 5.2129L19.9617 5.16368C19.8631 5.04779 19.7318 4.96448 19.5849 4.92473C19.4381 4.88499 19.2826 4.89067 19.1391 4.94103L17.2336 5.61837C16.5305 5.04181 15.7453 4.58712 14.8969 4.26837L14.5289 2.27618C14.5011 2.12628 14.4284 1.98838 14.3204 1.88079C14.2124 1.7732 14.0742 1.70102 13.9242 1.67384L13.8609 1.66212C12.6398 1.44181 11.3555 1.44181 10.1344 1.66212L10.0711 1.67384C9.92108 1.70102 9.78289 1.7732 9.67488 1.88079C9.56688 1.98838 9.49416 2.12628 9.4664 2.27618L9.09609 4.27775C8.25442 4.59656 7.47062 5.05101 6.77577 5.62306L4.85624 4.94103C4.71272 4.89027 4.55716 4.88438 4.41022 4.92415C4.26327 4.96392 4.13191 5.04747 4.03359 5.16368L3.9914 5.2129C3.17987 6.16944 2.54712 7.26424 2.12343 8.44493L2.10234 8.50587C1.99687 8.79884 2.08359 9.12697 2.32031 9.33087L3.87421 10.6574C3.80156 11.0981 3.7664 11.5481 3.7664 11.9957C3.7664 12.4457 3.80156 12.8957 3.87421 13.334L2.32031 14.6606C2.20451 14.7597 2.12164 14.8917 2.0827 15.0391C2.04377 15.1864 2.05062 15.3422 2.10234 15.4856L2.12343 15.5465C2.54765 16.7277 3.17577 17.8176 3.9914 18.7785L4.03359 18.8277C4.13215 18.9436 4.26352 19.027 4.41037 19.0667C4.55723 19.1064 4.71268 19.1008 4.85624 19.0504L6.77577 18.3684C7.47421 18.9426 8.25468 19.3973 9.09609 19.7137L9.4664 21.7152C9.49416 21.8651 9.56688 22.0031 9.67488 22.1106C9.78289 22.2182 9.92108 22.2904 10.0711 22.3176L10.1344 22.3293C11.3667 22.5508 12.6286 22.5508 13.8609 22.3293L13.9242 22.3176C14.0742 22.2904 14.2124 22.2182 14.3204 22.1106C14.4284 22.0031 14.5011 21.8651 14.5289 21.7152L14.8969 19.7231C15.745 19.4051 16.5345 18.949 17.2336 18.3731L19.1391 19.0504C19.2826 19.1012 19.4381 19.107 19.5851 19.0673C19.732 19.0275 19.8634 18.944 19.9617 18.8277L20.0039 18.7785C20.8195 17.8152 21.4476 16.7277 21.8719 15.5465L21.893 15.4856C21.9984 15.1973 21.9117 14.8692 21.675 14.6652ZM18.4758 10.9199C18.5344 11.2738 18.5648 11.6371 18.5648 12.0004C18.5648 12.3637 18.5344 12.727 18.4758 13.0809L18.3211 14.0207L20.0719 15.5184C19.8065 16.1298 19.4714 16.7087 19.0734 17.2434L16.8984 16.4723L16.1625 17.077C15.6023 17.5363 14.9789 17.8973 14.3039 18.1504L13.4109 18.4856L12.9914 20.759C12.3295 20.834 11.6612 20.834 10.9992 20.759L10.5797 18.4809L9.69374 18.141C9.02577 17.8879 8.40468 17.527 7.84921 17.0699L7.11327 16.4629L4.92421 17.241C4.52577 16.7043 4.19296 16.1254 3.92577 15.516L5.69531 14.0043L5.54296 13.0668C5.48671 12.7176 5.45624 12.3567 5.45624 12.0004C5.45624 11.6418 5.48437 11.2832 5.54296 10.934L5.69531 9.9965L3.92577 8.48478C4.19062 7.87306 4.52577 7.2965 4.92421 6.75978L7.11327 7.5379L7.84921 6.93087C8.40468 6.47384 9.02577 6.1129 9.69374 5.85978L10.582 5.52462L11.0016 3.2465C11.6601 3.1715 12.3328 3.1715 12.9937 3.2465L13.4133 5.51993L14.3062 5.85509C14.9789 6.10822 15.6047 6.46915 16.1648 6.92853L16.9008 7.53322L19.0758 6.76212C19.4742 7.29884 19.807 7.87775 20.0742 8.48712L18.3234 9.98478L18.4758 10.9199ZM12 7.64103C9.72187 7.64103 7.87499 9.4879 7.87499 11.766C7.87499 14.0442 9.72187 15.891 12 15.891C14.2781 15.891 16.125 14.0442 16.125 11.766C16.125 9.4879 14.2781 7.64103 12 7.64103ZM13.8562 13.6223C13.6128 13.8664 13.3234 14.0601 13.0049 14.192C12.6863 14.3239 12.3448 14.3916 12 14.391C11.2992 14.391 10.6406 14.1168 10.1437 13.6223C9.89958 13.3788 9.70596 13.0895 9.57403 12.7709C9.4421 12.4523 9.37446 12.1108 9.37499 11.766C9.37499 11.0652 9.64921 10.4067 10.1437 9.90978C10.6406 9.4129 11.2992 9.14103 12 9.14103C12.7008 9.14103 13.3594 9.4129 13.8562 9.90978C14.1004 10.1532 14.294 10.4426 14.426 10.7611C14.5579 11.0797 14.6255 11.4212 14.625 11.766C14.625 12.4668 14.3508 13.1254 13.8562 13.6223Z"
            className="fill-current"
        />
    ),
};

export const cloudStorage = {
    id: CLOUD_STORAGE,
    name: 'Cloud Storage',
    clientUrl: clientUrl('cloud-storage'),
    activeInSidebar: MANAGE_CLIENT,
    activeSubnavItem: CLOUD_STORAGE,
    isSpa: true,
    iconPath: (
        <path
            d="M484 443.1V528h-84.5c-4.1 0-7.5 3.1-7.5 7v42c0 3.8 3.4 7 7.5 7H484v84.9c0 3.9 3.2 7.1 7 7.1h42c3.9 0 7-3.2 7-7.1V584h84.5c4.1 0 7.5-3.2 7.5-7v-42c0-3.9-3.4-7-7.5-7H540v-84.9c0-3.9-3.1-7.1-7-7.1h-42c-3.8 0-7 3.2-7 7.1zm396-144.7H521L403.7 186.2a8.15 8.15 0 00-5.5-2.2H144c-17.7 0-32 14.3-32 32v592c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V330.4c0-17.7-14.3-32-32-32zM840 768H184V256h188.5l119.6 114.4H840V768z"
            className="fill-current"
        />
    ),
};

export const EVENTS = 'events';
export const events = {
    id: EVENTS,
    name: 'Events',
    clientUrl: clientUrl('events'),
    activeInSidebar: MANAGE_CLIENT,
    isSpa: true,
    // antd alert-outlined event
    iconPath: (
        <path
            d="M193 796c0 17.7 14.3 32 32 32h574c17.7 0 32-14.3 32-32V563c0-176.2-142.8-319-319-319S193 386.8 193 563v233zm72-233c0-136.4 110.6-247 247-247s247 110.6 247 247v193H404V585c0-5.5-4.5-10-10-10h-44c-5.5 0-10 4.5-10 10v171h-75V563zm-48.1-252.5l39.6-39.6c3.1-3.1 3.1-8.2 0-11.3l-67.9-67.9a8.03 8.03 0 00-11.3 0l-39.6 39.6a8.03 8.03 0 000 11.3l67.9 67.9c3.1 3.1 8.1 3.1 11.3 0zm669.6-79.2l-39.6-39.6a8.03 8.03 0 00-11.3 0l-67.9 67.9a8.03 8.03 0 000 11.3l39.6 39.6c3.1 3.1 8.2 3.1 11.3 0l67.9-67.9c3.1-3.2 3.1-8.2 0-11.3zM832 892H192c-17.7 0-32 14.3-32 32v24c0 4.4 3.6 8 8 8h688c4.4 0 8-3.6 8-8v-24c0-17.7-14.3-32-32-32zM484 180h56c4.4 0 8-3.6 8-8V76c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v96c0 4.4 3.6 8 8 8z"
            className="fill-current"
        />
    ),
};

export const ADMIN = 'admin';
export const admin = {
    id: ADMIN,
    name: 'Admin',
    // there is not real client specific area of the admin, so point it to the
    // manage area of a client, closes we can get.
    clientUrl: manageClient.clientUrl,
    activeInSidebar: MANAGE_CLIENT,
    isSpa: true,
    // antd ControlOutlines
    iconPath: (
        <path
            d="M880 112H144c-17.7 0-32 14.3-32 32v736c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V144c0-17.7-14.3-32-32-32zm-40 728H184V184h656v656zM340 683v77c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8v-77c-10.1 3.3-20.8 5-32 5s-21.9-1.8-32-5zm64-198V264c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v221c10.1-3.3 20.8-5 32-5s21.9 1.8 32 5zm-64 198c10.1 3.3 20.8 5 32 5s21.9-1.8 32-5c41.8-13.5 72-52.7 72-99s-30.2-85.5-72-99c-10.1-3.3-20.8-5-32-5s-21.9 1.8-32 5c-41.8 13.5-72 52.7-72 99s30.2 85.5 72 99zm.1-115.7c.3-.6.7-1.2 1-1.8v-.1l1.2-1.8c.1-.2.2-.3.3-.5.3-.5.7-.9 1-1.4.1-.1.2-.3.3-.4.5-.6.9-1.1 1.4-1.6l.3-.3 1.2-1.2.4-.4c.5-.5 1-.9 1.6-1.4.6-.5 1.1-.9 1.7-1.3.2-.1.3-.2.5-.3.5-.3.9-.7 1.4-1 .1-.1.3-.2.4-.3.6-.4 1.2-.7 1.9-1.1.1-.1.3-.1.4-.2.5-.3 1-.5 1.6-.8l.6-.3c.7-.3 1.3-.6 2-.8.7-.3 1.4-.5 2.1-.7.2-.1.4-.1.6-.2.6-.2 1.1-.3 1.7-.4.2 0 .3-.1.5-.1.7-.2 1.5-.3 2.2-.4.2 0 .3 0 .5-.1.6-.1 1.2-.1 1.8-.2h.6c.8 0 1.5-.1 2.3-.1s1.5 0 2.3.1h.6c.6 0 1.2.1 1.8.2.2 0 .3 0 .5.1.7.1 1.5.2 2.2.4.2 0 .3.1.5.1.6.1 1.2.3 1.7.4.2.1.4.1.6.2.7.2 1.4.4 2.1.7.7.2 1.3.5 2 .8l.6.3c.5.2 1.1.5 1.6.8.1.1.3.1.4.2.6.3 1.3.7 1.9 1.1.1.1.3.2.4.3.5.3 1 .6 1.4 1 .2.1.3.2.5.3.6.4 1.2.9 1.7 1.3s1.1.9 1.6 1.4l.4.4 1.2 1.2.3.3c.5.5 1 1.1 1.4 1.6.1.1.2.3.3.4.4.4.7.9 1 1.4.1.2.2.3.3.5l1.2 1.8s0 .1.1.1a36.18 36.18 0 015.1 18.5c0 6-1.5 11.7-4.1 16.7-.3.6-.7 1.2-1 1.8 0 0 0 .1-.1.1l-1.2 1.8c-.1.2-.2.3-.3.5-.3.5-.7.9-1 1.4-.1.1-.2.3-.3.4-.5.6-.9 1.1-1.4 1.6l-.3.3-1.2 1.2-.4.4c-.5.5-1 .9-1.6 1.4-.6.5-1.1.9-1.7 1.3-.2.1-.3.2-.5.3-.5.3-.9.7-1.4 1-.1.1-.3.2-.4.3-.6.4-1.2.7-1.9 1.1-.1.1-.3.1-.4.2-.5.3-1 .5-1.6.8l-.6.3c-.7.3-1.3.6-2 .8-.7.3-1.4.5-2.1.7-.2.1-.4.1-.6.2-.6.2-1.1.3-1.7.4-.2 0-.3.1-.5.1-.7.2-1.5.3-2.2.4-.2 0-.3 0-.5.1-.6.1-1.2.1-1.8.2h-.6c-.8 0-1.5.1-2.3.1s-1.5 0-2.3-.1h-.6c-.6 0-1.2-.1-1.8-.2-.2 0-.3 0-.5-.1-.7-.1-1.5-.2-2.2-.4-.2 0-.3-.1-.5-.1-.6-.1-1.2-.3-1.7-.4-.2-.1-.4-.1-.6-.2-.7-.2-1.4-.4-2.1-.7-.7-.2-1.3-.5-2-.8l-.6-.3c-.5-.2-1.1-.5-1.6-.8-.1-.1-.3-.1-.4-.2-.6-.3-1.3-.7-1.9-1.1-.1-.1-.3-.2-.4-.3-.5-.3-1-.6-1.4-1-.2-.1-.3-.2-.5-.3-.6-.4-1.2-.9-1.7-1.3s-1.1-.9-1.6-1.4l-.4-.4-1.2-1.2-.3-.3c-.5-.5-1-1.1-1.4-1.6-.1-.1-.2-.3-.3-.4-.4-.4-.7-.9-1-1.4-.1-.2-.2-.3-.3-.5l-1.2-1.8v-.1c-.4-.6-.7-1.2-1-1.8-2.6-5-4.1-10.7-4.1-16.7s1.5-11.7 4.1-16.7zM620 539v221c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V539c-10.1 3.3-20.8 5-32 5s-21.9-1.8-32-5zm64-198v-77c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v77c10.1-3.3 20.8-5 32-5s21.9 1.8 32 5zm-64 198c10.1 3.3 20.8 5 32 5s21.9-1.8 32-5c41.8-13.5 72-52.7 72-99s-30.2-85.5-72-99c-10.1-3.3-20.8-5-32-5s-21.9 1.8-32 5c-41.8 13.5-72 52.7-72 99s30.2 85.5 72 99zm.1-115.7c.3-.6.7-1.2 1-1.8v-.1l1.2-1.8c.1-.2.2-.3.3-.5.3-.5.7-.9 1-1.4.1-.1.2-.3.3-.4.5-.6.9-1.1 1.4-1.6l.3-.3 1.2-1.2.4-.4c.5-.5 1-.9 1.6-1.4.6-.5 1.1-.9 1.7-1.3.2-.1.3-.2.5-.3.5-.3.9-.7 1.4-1 .1-.1.3-.2.4-.3.6-.4 1.2-.7 1.9-1.1.1-.1.3-.1.4-.2.5-.3 1-.5 1.6-.8l.6-.3c.7-.3 1.3-.6 2-.8.7-.3 1.4-.5 2.1-.7.2-.1.4-.1.6-.2.6-.2 1.1-.3 1.7-.4.2 0 .3-.1.5-.1.7-.2 1.5-.3 2.2-.4.2 0 .3 0 .5-.1.6-.1 1.2-.1 1.8-.2h.6c.8 0 1.5-.1 2.3-.1s1.5 0 2.3.1h.6c.6 0 1.2.1 1.8.2.2 0 .3 0 .5.1.7.1 1.5.2 2.2.4.2 0 .3.1.5.1.6.1 1.2.3 1.7.4.2.1.4.1.6.2.7.2 1.4.4 2.1.7.7.2 1.3.5 2 .8l.6.3c.5.2 1.1.5 1.6.8.1.1.3.1.4.2.6.3 1.3.7 1.9 1.1.1.1.3.2.4.3.5.3 1 .6 1.4 1 .2.1.3.2.5.3.6.4 1.2.9 1.7 1.3s1.1.9 1.6 1.4l.4.4 1.2 1.2.3.3c.5.5 1 1.1 1.4 1.6.1.1.2.3.3.4.4.4.7.9 1 1.4.1.2.2.3.3.5l1.2 1.8v.1a36.18 36.18 0 015.1 18.5c0 6-1.5 11.7-4.1 16.7-.3.6-.7 1.2-1 1.8v.1l-1.2 1.8c-.1.2-.2.3-.3.5-.3.5-.7.9-1 1.4-.1.1-.2.3-.3.4-.5.6-.9 1.1-1.4 1.6l-.3.3-1.2 1.2-.4.4c-.5.5-1 .9-1.6 1.4-.6.5-1.1.9-1.7 1.3-.2.1-.3.2-.5.3-.5.3-.9.7-1.4 1-.1.1-.3.2-.4.3-.6.4-1.2.7-1.9 1.1-.1.1-.3.1-.4.2-.5.3-1 .5-1.6.8l-.6.3c-.7.3-1.3.6-2 .8-.7.3-1.4.5-2.1.7-.2.1-.4.1-.6.2-.6.2-1.1.3-1.7.4-.2 0-.3.1-.5.1-.7.2-1.5.3-2.2.4-.2 0-.3 0-.5.1-.6.1-1.2.1-1.8.2h-.6c-.8 0-1.5.1-2.3.1s-1.5 0-2.3-.1h-.6c-.6 0-1.2-.1-1.8-.2-.2 0-.3 0-.5-.1-.7-.1-1.5-.2-2.2-.4-.2 0-.3-.1-.5-.1-.6-.1-1.2-.3-1.7-.4-.2-.1-.4-.1-.6-.2-.7-.2-1.4-.4-2.1-.7-.7-.2-1.3-.5-2-.8l-.6-.3c-.5-.2-1.1-.5-1.6-.8-.1-.1-.3-.1-.4-.2-.6-.3-1.3-.7-1.9-1.1-.1-.1-.3-.2-.4-.3-.5-.3-1-.6-1.4-1-.2-.1-.3-.2-.5-.3-.6-.4-1.2-.9-1.7-1.3s-1.1-.9-1.6-1.4l-.4-.4-1.2-1.2-.3-.3c-.5-.5-1-1.1-1.4-1.6-.1-.1-.2-.3-.3-.4-.4-.4-.7-.9-1-1.4-.1-.2-.2-.3-.3-.5l-1.2-1.8v-.1c-.4-.6-.7-1.2-1-1.8-2.6-5-4.1-10.7-4.1-16.7s1.5-11.7 4.1-16.7z"
            className="fill-current"
        />
    ),
};

export const NOTIFICATIONS = 'notifications';
export const notifications = {
    id: NOTIFICATIONS,
    name: 'Notifications',
    clientUrl: clientUrl('notifications'),
    activeInSidebar: MANAGE_CLIENT,
    isSpa: true,
    // antd mail icon
    iconPath: (
        <path
            d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 110.8V792H136V270.8l-27.6-21.5 39.3-50.5 42.8 33.3h643.1l42.8-33.3 39.3 50.5-27.7 21.5zM833.6 232L512 482 190.4 232l-42.8-33.3-39.3 50.5 27.6 21.5 341.6 265.6a55.99 55.99 0 0 0 68.7 0L888 270.8l27.6-21.5-39.3-50.5-42.7 33.2z"
            className="fill-current"
        />
    ),
};

export const defaultApplication = DASHBOARDS;

// This is the order of the applications in the alli sidebar and it's here
// so we have it in a single place, right next to all the constants.
export const menu = [
    dashboards,
    data,
    strategyAndPlanning,
    actions,
    audiences,
    creative,
    products,
    marketplace,
    myapps,
];

export const bottomMenu = [manageClient];

export function mapMenu(fn) {
    return menu.map(app => fn(app, app.id));
}

export function mapBottomMenu(fn) {
    return bottomMenu.map(app => fn(app, app.id));
}

export function mapAllMenu(fn) {
    const out = mapMenu(fn);
    return out.concat(mapBottomMenu(fn));
}

/**
 * Render the application's `clientUrl` for the given client.
 *
 * @param object client the client to render the link for
 * @param object application the application (one of the lowercase constants above)
 * @return string
 */
export function renderClientUrl({ id, slug }, { id: appId, clientUrl, isSpa }) {
    if (typeof clientUrl !== 'string') {
        throw new Error(
            `application ${appId} does not have an "clientUrl" property`,
        );
    }

    return clientUrl.replace(clientPlaceholder, isSpa ? slug : id);
}
